import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Select, Input, DatePicker, Button, Tooltip, Alert, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import BasicLayout from '../../components/layout/BasicLayout';
import { getDictionaries, getValues, normalizeApiError } from './analyticsIndicatorsApi';
import { fmtDate, fmtDateTime, fmtNumber } from './periodUtils';
import IndicatorValueModal from './IndicatorValueModal';
import IndicatorValueView from './IndicatorValueView';
import './analyticsIndicators.css';

const { Option } = Select;
const PAGE_SIZES = [10, 25, 50, 100];
const USER_ACTIONS_PATH = '/admin/user-actions';

export default function AnalyticsIndicatorsPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  // Язык ДАННЫХ: суффиксы полей бэкенда — ru | kk (kk = всё, что не ru).
  const dataLang = (i18n.language || '').toLowerCase().startsWith('ru') ? 'ru' : 'kk';

  const [dict, setDict] = useState({ indicators: [], period_types: [] });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    indicatorId: null,
    periodType: null,
    dateFrom: null,
    dateTo: null,
  });
  const [searchInput, setSearchInput] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const indicators = useMemo(
    () => dict.indicators.slice().sort((a, b) => a.sort_order - b.sort_order),
    [dict.indicators]
  );
  const indById = useMemo(() => {
    const m = {};
    dict.indicators.forEach((it) => {
      m[it.id] = it;
    });
    return m;
  }, [dict.indicators]);
  const nm = (o, base) => (o ? o[`${base}_${dataLang}`] : null);

  // Справочники — один раз при открытии раздела.
  useEffect(() => {
    let alive = true;
    getDictionaries()
      .then((d) => {
        if (alive) setDict(d);
      })
      .catch((err) => {
        const info = normalizeApiError(err);
        if (info.forbidden) setForbidden(true);
        else setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const loadValues = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    getValues({
      page,
      size,
      search: filters.search,
      indicatorId: filters.indicatorId,
      periodType: filters.periodType,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    })
      .then((res) => {
        setRows(res.rows);
        setTotal(res.total);
      })
      .catch((err) => {
        const info = normalizeApiError(err);
        if (info.forbidden) setForbidden(true);
        else setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [page, size, filters]);

  useEffect(() => {
    if (!forbidden) loadValues();
  }, [loadValues, forbidden]);

  // Поиск с задержкой 400 мс.
  const searchTimer = useRef(null);
  const onSearchInput = (e) => {
    const v = e.target.value;
    setSearchInput(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      setFilters((f) => ({ ...f, search: v.trim() }));
    }, 400);
  };

  const resetFilters = () => {
    setSearchInput('');
    setPage(1);
    setFilters({ search: '', indicatorId: null, periodType: null, dateFrom: null, dateTo: null });
  };

  // Проверка дубля по загруженной странице (клиентская, предварительная; итог — за бэкендом 409).
  const checkDuplicate = useCallback(
    (indicatorId, periodEndIso) =>
      rows.some((r) => r.indicator_id === indicatorId && r.period_end === periodEndIso),
    [rows]
  );

  const onSaved = () => {
    setModalOpen(false);
    setPage(1);
    if (page === 1) loadValues(); // если уже на первой странице — принудительно
    notification.success({
      message: t('analyticsIndicators.toast.ok'),
      description: (
        <span>
          {t('analyticsIndicators.toast.sub')}{' '}
          <a onClick={() => history.push(USER_ACTIONS_PATH)}>
            {t('analyticsIndicators.toast.link')}
          </a>
        </span>
      ),
      placement: 'bottomRight',
      duration: 4,
    });
  };

  const columns = useMemo(
    () => [
      {
        title: t('analyticsIndicators.col.indicator'),
        key: 'indicator',
        render: (_, r) => {
          const it = indById[r.indicator_id];
          return (
            <div>
              <Tooltip title={nm(it, 'name')}>
                <div className="ai-cell-name">{nm(it, 'name')}</div>
              </Tooltip>
              {nm(it, 'sector') && <div className="ai-cell-sub">{nm(it, 'sector')}</div>}
            </div>
          );
        },
      },
      {
        title: t('analyticsIndicators.col.periodType'),
        key: 'periodType',
        width: 130,
        render: (_, r) => t(`analyticsIndicators.pt.${r.period_type}`),
      },
      {
        title: t('analyticsIndicators.col.period'),
        key: 'period',
        render: (_, r) => (
          <Tooltip title={`${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}`}>
            <span>{r[`period_label_${dataLang}`]}</span>
          </Tooltip>
        ),
      },
      {
        title: t('analyticsIndicators.col.value'),
        key: 'value',
        align: 'right',
        width: 160,
        render: (_, r) => {
          const s = fmtNumber(r.value);
          return s === null ? (
            <span className="ai-dash">—</span>
          ) : (
            <span className="ai-num">{s}</span>
          );
        },
      },
      {
        title: t('analyticsIndicators.col.unit'),
        key: 'unit',
        width: 110,
        render: (_, r) => nm(indById[r.indicator_id], 'unit'),
      },
      {
        title: t('analyticsIndicators.col.created'),
        key: 'created',
        width: 190,
        render: (_, r) => (
          <div className="ai-dt">
            {fmtDateTime(r.created_at)}
            <div className="ai-cell-sub">{r[`created_by_${dataLang}`]}</div>
          </div>
        ),
      },
    ],
    [t, i18n.language, indById, dataLang]
  );

  if (forbidden) {
    return (
      <BasicLayout>
        <div className="ai-page">
          <h1 className="ai-page__title">{t('analyticsIndicators.title')}</h1>
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
            message={t('analyticsIndicators.err.forbidden')}
          />
        </div>
      </BasicLayout>
    );
  }

  return (
    <BasicLayout>
      <div className="ai-page">
        <div className="ai-page__head">
          <div>
            <h1 className="ai-page__title">{t('analyticsIndicators.title')}</h1>
          </div>
          <Button type="primary" onClick={() => setModalOpen(true)}>
            {t('analyticsIndicators.create')}
          </Button>
        </div>

        {/* Фильтры и поиск */}
        <div className="ai-filters">
          <div className="ai-fl ai-fl--grow">
            <label>{t('analyticsIndicators.flSearch')}</label>
            <Input
              allowClear
              value={searchInput}
              placeholder={t('analyticsIndicators.phSearch')}
              onChange={onSearchInput}
            />
          </div>
          <div className="ai-fl">
            <label>{t('analyticsIndicators.flIndicator')}</label>
            <Select
              allowClear
              showSearch
              style={{ minWidth: 240 }}
              value={filters.indicatorId || undefined}
              placeholder={t('analyticsIndicators.allItems')}
              optionFilterProp="label"
              onChange={(v) => {
                setPage(1);
                setFilters((f) => ({ ...f, indicatorId: v || null }));
              }}
            >
              {indicators.map((it) => {
                const label = nm(it, 'name') + (nm(it, 'sector') ? ` · ${nm(it, 'sector')}` : '');
                return (
                  <Option key={it.id} value={it.id} label={label}>
                    {label}
                  </Option>
                );
              })}
            </Select>
          </div>
          <div className="ai-fl">
            <label>{t('analyticsIndicators.flPeriodType')}</label>
            <Select
              allowClear
              style={{ minWidth: 150 }}
              value={filters.periodType || undefined}
              placeholder={t('analyticsIndicators.allItems')}
              onChange={(v) => {
                setPage(1);
                setFilters((f) => ({ ...f, periodType: v || null }));
              }}
            >
              {['year', 'half', 'quarter', 'month'].map((k) => (
                <Option key={k} value={k}>
                  {t(`analyticsIndicators.pt.${k}`)}
                </Option>
              ))}
            </Select>
          </div>
          <div className="ai-fl">
            <label>{t('analyticsIndicators.flFrom')}</label>
            <DatePicker
              format="DD.MM.YYYY"
              style={{ width: 140 }}
              onChange={(m) => {
                setPage(1);
                setFilters((f) => ({ ...f, dateFrom: m ? m.format('YYYY-MM-DD') : null }));
              }}
            />
          </div>
          <div className="ai-fl">
            <label>{t('analyticsIndicators.flTo')}</label>
            <DatePicker
              format="DD.MM.YYYY"
              style={{ width: 140 }}
              onChange={(m) => {
                setPage(1);
                setFilters((f) => ({ ...f, dateTo: m ? m.format('YYYY-MM-DD') : null }));
              }}
            />
          </div>
          <Button onClick={resetFilters}>{t('analyticsIndicators.reset')}</Button>
        </div>

        {loadError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('analyticsIndicators.err.load')}
            action={
              <Button size="small" onClick={loadValues}>
                {t('analyticsIndicators.retry')}
              </Button>
            }
          />
        )}

        <Table
          className="ai-table"
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={rows}
          onRow={(record) => ({
            onClick: () => {
              if (!modalOpen) setViewRow(record);
            },
          })}
          locale={{
            emptyText: (
              <div className="ai-empty">
                <b>{t('analyticsIndicators.emptyTitle')}</b>
                <span>{t('analyticsIndicators.emptyHint')}</span>
              </div>
            ),
          }}
          pagination={{
            current: page,
            pageSize: size,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZES.map(String),
            showTotal: (tot) => `${t('analyticsIndicators.totalLabel')} ${tot}`,
            locale: { items_per_page: `/ ${t('analyticsIndicators.perPage')}` },
            onChange: (p, ps) => {
              setPage(p);
              setSize(ps);
            },
          }}
        />
      </div>

      <IndicatorValueModal
        open={modalOpen}
        indicators={indicators}
        dataLang={dataLang}
        checkDuplicate={checkDuplicate}
        onClose={() => setModalOpen(false)}
        onSaved={onSaved}
        onForbidden={() => setForbidden(true)}
      />

      <IndicatorValueView
        open={!!viewRow}
        row={viewRow}
        indicators={indicators}
        dataLang={dataLang}
        onClose={() => setViewRow(null)}
      />
    </BasicLayout>
  );
}
